import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
// Core
import {OperationRegisterService} from '@core/services/core/operation-register.service';
import {NavigationHelper} from '@core/services/core/navigation-helper.service';
import {BaseService} from '@core/services/core/base.service';

import {Family, FamilyRelationshipsType, Member} from '@models/family';
import {FamiliesOperation} from '@models/operation-register';
import { Platform } from '@ionic/angular';
import { METS_APP_PREFIX, METS_WEB_PREFIX } from '@core/services/core/analytics/analytics-context';

@Injectable({
  providedIn: 'root'
})
export class FamiliesService {

  public familyMembers: BehaviorSubject<Member[]> = new BehaviorSubject([]);
  public progressObs: BehaviorSubject<number> = new BehaviorSubject(10);
  public userSubject: BehaviorSubject<Member> = new BehaviorSubject<Member>(null);
  public familySubject: BehaviorSubject<Family> = new BehaviorSubject<Family>(null);
  public isUpdateMembers: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isUpdateFamily: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private hasGoneThroughTheFamiliesHelper = false;

  constructor(
    private or: OperationRegisterService,
    private platform: Platform,
    private nav: NavigationHelper,
    private base: BaseService,
    private http: HttpClient
  ) {

  }

  public get user() {
    return this.userSubject.asObservable();
  }

  public get members() {
    return this.familyMembers.asObservable();
  }

  public get progress() {
    return this.progressObs.asObservable();
  }

  public get family() {
    return this.familySubject.asObservable();
  }

  public get getIsUpdateMembers() {
    return this.isUpdateMembers.asObservable();
  }

  public get getIsUpdateFamily() {
    return this.isUpdateFamily.asObservable();
  }

  public get getHasGoneThroughTheFamiliesHelper() {
    return this.hasGoneThroughTheFamiliesHelper;
  }

  public setProgress(progress: number) {
    this.progressObs.next(progress);
  }

  public setUser(user: Member) {
    this.userSubject.next(user);
  }

  public setMembers(members: Member[]) {
    this.familyMembers.next(members);
  }

  public setFamily(family: Family) {
    this.familySubject.next(family);
  }

  public setIsUpdateMembers(value) {
    this.isUpdateMembers.next(value);
  }

  public setIsUpdateFamily(value) {
    this.isUpdateFamily.next(value);
  }

  public setHasGoneThroughTheFamiliesHelper(value) {
    this.hasGoneThroughTheFamiliesHelper = value;
  }

  /**
   * Back returns the descriptive in the 'relationship' field but we need the code for the selector
   */
  public setRelationship(member) {
    const relationship = member.relationship.toString().toUpperCase();
    if (relationship.includes('HIJO') || relationship.includes('HIJA')) {
      member.relationshipDescription = member.relationship;
      member.relationship = FamilyRelationshipsType.HM;
    }
    if (relationship.includes('PAREJA')) {
      member.relationshipDescription = member.relationship;
      member.relationship = FamilyRelationshipsType.RR;
    }
    if (relationship.includes('TUTELADO') || relationship.includes('TUTELADA')) {
      member.relationshipDescription = member.relationship;
      member.relationship = FamilyRelationshipsType.TL;
    }
  }

  public getOneMember(documentType: string, documentId: string, clientCode: string) {
    return this.familyMembers.pipe(
      take(1),
      map((members: Member[]) => {
        const m = members.find((member: Member) => member.documentNumber.toString().toLowerCase() === documentId.toString().toLowerCase()
            && member.documentType === documentType && member.clientCode === clientCode);
        return {
          birthdate: m.birthdate,
          clientCode: m.clientCode,
          codFamily: m.codFamily,
          consentStatus: m.consentStatus,
          creator: m.creator,
          documentNumber: m.documentNumber,
          documentType: m.documentType,
          email: m.email,
          isEditable: m.isEditable,
          editableDocument: m.editableDocument,
          name: m.name,
          phone: m.phone,
          relationship: m.relationship,
          relationshipDescription: m.relationshipDescription,
          roleCode: m.roleCode,
          surname1: m.surname1,
          surname2: m.surname2,
          validate: m.validate
        };
      }),
    );
  }

  public setAnalytics(members, screenTitle?, step?, isCreator?, isEdit?) {
    let familyNumber = (members) ? members.length.toString() : '0';
    let isFamilyPending = false;
    let familyAcceptNumber = 0;
    let familyBirthday = '';
    if (members) {
      for (const m of members) {
        const birthdate = (m.birthdate) ? m.birthdate : '';
        if (birthdate !== '') {
          familyBirthday += birthdate + ';';
        }
        if (isCreator && m.consentStatus === 'PC') {
          isFamilyPending = true;
        }
      }
    }
    // If the user is a creator, only the users who have accepted will be shown if we have pending invitations
    if (isCreator && isFamilyPending && members) {
      for (const m of members) {
        if (m.consentStatus === 'CF') {
          familyAcceptNumber += 1;
        }
      }
      familyNumber = familyAcceptNumber.toString();
    }
    const metsPrefix = this.platform.is('cordova') ? METS_APP_PREFIX : METS_WEB_PREFIX;
    const objReturn = {
      ScreenTitle: `${metsPrefix}:mi familia`,
      CD28_area_clientes: 'area de clientes',
      CD140_family_birthdays: 'family:' + familyBirthday,
      CD152_family_numbers: familyNumber
    };
    if (screenTitle) {
      objReturn['ScreenTitle'] += screenTitle;
      if (screenTitle !== 'usuario sin familia') {
        objReturn['CD131_family'] = 'family';
        if (isEdit) {
          objReturn['CD132_family_management'] = 'family management';
        } else {
          objReturn['CD132_create_family'] = 'create family';
        }
      }
    }
    if (step) {
      if (step.screenTitle) {
        objReturn['ScreenTitle'] += step.screenTitle;
      }
      if (step.step) {
        objReturn['CD133_step'] = step.step;
      }
    }
    return objReturn;
  }

  public getStructureFamily(userAuth, members, codOperation, error?) {
    if (codOperation === FamiliesOperation.ACEPTA_CONDICIONES) {
      const auxMembers = [];
      for (const m of members) {
        auxMembers.push({
          'Parentesco:': m.relationship,
          'Nombre:': m.name,
          'Primer apellido:': m.surname1,
          'Segundo apellido:': m.surname2,
          'Tipo Documento:': m.documentType,
          'Documento identificativo:': m.documentNumber,
          'Fecha de nacimiento:': m.birthdate,
          'Móvil:': m.phone,
          'Email:': m.email
        });
      }
      return JSON.stringify({
        'Datos del gestor:':
          {
            'Tipo Documento:': userAuth.documentType,
            'Documento identificativo:': userAuth.documentNumber,
            'Nombre:': userAuth.name,
            'Primer apellido:': userAuth.surname1,
            'Segundo apellido:': userAuth.surname2,
            'Datos de los familiares:' : auxMembers
          }
      });
    } else {
      return JSON.stringify({
        'Datos del cliente RIC:':
          {
            'Documento identificativo:': userAuth.documentNumber,
            'Error:': error
          }
      });
    }
  }

  public getFamily() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families`;
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.get<Family>(url, {...headers, params: headerParams});
  }

  public getFamilyDetail(id: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families/${id}`;
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.get<Family>(url, {...headers, params: headerParams});
  }

  public createFamily() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families`;
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.post<any>(url, null, {...headers, params: headerParams});
  }

  public removeFamily(id: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families/${id}`;
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.delete<any>(url, {...headers, params: headerParams});
  }

  public addMember(id: string, body: any) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families/${id}/members`;
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.post<any>(url, body, {...headers, params: headerParams});
  }

  public modifyFamilyData(id: string, clientCode: string, body: object) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families/${id}/members/${clientCode}`;
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.post<Member>(url, body, {...headers, params: headerParams});
  }

  public removeMember(id: string, clientCode: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families/${id}/members/${clientCode}`;
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.delete<any>(url, {...headers, params: headerParams});
  }

  public signFamily(id: string, advertisingAccepted: boolean) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families/${id}/sign`;
    const body = {advertisingAccepted};
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.post<any>(url, body, {...headers, params: headerParams});
  }

  public consentInvitation(id: string, clientCode: string, consentAccepted: boolean, advertisingAccepted: boolean) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families/${id}/members/${clientCode}/consent`;
    const body = {consentAccepted, advertisingAccepted};
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.post<any>(url, body, {...headers, params: headerParams});
  }

  public validateMember(id: string, body: any) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/families/${id}/client/validate`;
    const headers = this.base.getPrivateOIMHeaders({cacheResponse: false, clearCache: true});
    const headerParams = this.or.setFamiliesOperationParams(this.userSubject['_value']);
    return this.http.post<any>(url, body, {...headers, params: headerParams});
  }

}
